<template>
	<div class="collapsible-container">
		<div class="header" @click="toggle">
			<div class="title-container">
				<!-- If there's a title slot, use it -->
				<div v-if="$slots.title" class="title">
					<slot name="title" />
				</div>
				<!-- Otherwise, fall back to the title prop -->
				<span v-else-if="title" class="title">{{ title }}</span>

				<!-- If there's a description slot, use it -->
				<div v-if="$slots.description" class="description">
					<slot name="description" />
				</div>
				<!-- Otherwise, fall back to the description prop -->
				<span v-else-if="description" class="description">{{
					description
				}}</span>
			</div>
			<ChevronIcon
				v-if="!isLoading && !hideChevron"
				:up="expanded"
				class="chevron-icon"
				type="solid"
			/>
			<div v-else-if="isLoading" class="loading-spinner-container">
				<IosSpinner class="loading-spinner" spinnerColor="var(--neutral-500)" />
			</div>
		</div>
		<transition name="collapse">
			<div v-if="expanded && !isLoading" class="content">
				<slot />
			</div>
		</transition>
	</div>
</template>

<script setup>
import { ref } from 'vue'
import { ChevronIcon } from '@/icons'
import IosSpinner from '@/components/IosSpinner'

const props = defineProps({
	title: { type: String, default: '' },
	description: { type: String, default: '' },
	initiallyExpanded: { type: Boolean, default: true },
	isLoading: { type: Boolean, default: false },
	hideChevron: { type: Boolean, default: false },
})

const expanded = props.hideChevron ? ref(false) : ref(props.initiallyExpanded)

function toggle() {
	if (props.isLoading || props.hideChevron) {
		return
	}
	expanded.value = !expanded.value
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';
.collapsible-container {
	padding: 20px 0;

	.header {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		cursor: pointer;
		font-weight: 600;

		.title-container {
			display: flex;
			flex-direction: column;
			gap: 4px;
		}

		.title {
			font-family: 'Avenir-Heavy';
			color: var(--neutral-800);
			font-size: 22px;
		}

		.description {
			color: var(--neutral-600);
			font-size: 14px;
			font-weight: normal;
			font-family: 'Avenir-Roman';
		}

		.chevron-icon {
			color: var(--neutral-400);
			font-size: 20px;
			margin-top: 2px;
		}

		.loading-spinner-container {
			display: flex;
			align-items: center;
			margin-top: 2px;
			:deep(.loading-spinner) {
				position: static;
			}
			:deep(#mainSpinner) {
				height: 30px;
				width: 30px;
				background: var(--neutral100) !important;
			}
		}
	}

	.content {
		margin-top: 0.75rem;
		overflow: hidden;
	}

	.collapse-enter-active,
	.collapse-leave-active {
		transition: all 0.2s ease-in-out;
		max-height: 500px; /* Adjust as needed for max content height */
		opacity: 1;
	}

	.collapse-enter-from,
	.collapse-leave-to {
		max-height: 0;
		opacity: 0;
	}
}
</style>
